import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Translate } from 'react-translated';
import { withStyles, MuiThemeProvider } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import RootRef from '@material-ui/core/RootRef';
import Measure from 'react-measure';

import Box from './Box';
import AvailabilityButton from './AvailabilityButton';
import DatePicker from './DatePicker';
import GuestOptions from './GuestOptions';
import DateRange from '../utils/date-range';
import { emit } from '../utils/emitter';
import { createTheme, availableColors } from '../utils/theme';
import { format, supportsChildrenCount } from '../utils/formatters';

const styles = (theme) => ({
  container: {
    maxWidth: theme.breakpoints.values.lg,
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  dateColumn: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  guestColumn: {
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  }
});

class HotelPlugin extends Component {
  constructor(props) {
    super(props);

    this.state = {
      guestOptions: {
        roomCount: 1,
        adultCount: 2,
        childrenCount: 0
      },
      dateRange: new DateRange(),
      height: 0,
      loading: false
    };

    this.checkOutDatePickerRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('visibilitychange', this.handleVisibilityChange);
    // The current embed.js ignores this. Cached copies of the old embed.js only act on `hp-book` after
    // `hp-connect` has set them up, so keep sending it until those copies have expired.
    window.parent.postMessage({ type: 'hp-connect', source: this.props.url }, '*');
  }

  handleVisibilityChange = () => {
    if (!document.hidden) {
      this.setState({ loading: false });
    }
  };

  componentWillUnmount() {
    window.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  expand = () => {
    emit('resize', '100%');
  };

  collapse = () => {
    const { height } = this.state;

    emit('resize', `${height}px`);
  };

  onCheckInDatePickerChange = (value) => {
    const { dateRange } = this.state;

    if (dateRange.isSameStart(value)) {
      this.collapse();

      return;
    }

    this.onDateRangeChange('start', value);

    this.checkOutDatePickerRef.current.open({
      currentTarget: ReactDOM.findDOMNode(this.checkOutDatePickerRef.current)
    });
  };

  onCheckOutDatePickerChange = (value) => {
    const { dateRange } = this.state;

    if (dateRange.isSameEnd(value)) {
      this.collapse();

      return;
    }

    this.onDateRangeChange('end', value);
    this.collapse();
  };

  onDateRangeChange = (type, value) => {
    const { dateRange } = this.state;
    dateRange[type] = value;

    this.setState((state) => ({ ...state, dateRange }));
  };

  onResize = (rect) => {
    const { height } = this.state;

    const newHeight = rect.bounds.height;

    if (height !== newHeight) {
      this.setState(
        (state) => ({ ...state, height: newHeight }),
        () => emit('resize', `${newHeight}px`)
      );
    }
  };

  onGuesOptionsChange = (guestOptions) => {
    this.setState((state) => ({ ...state, guestOptions }));
  };

  onAvailabilityCheck = async () => {
    let { url, language, color } = this.props;
    const {
      dateRange,
      guestOptions: { roomCount, adultCount, childrenCount }
    } = this.state;
    const startDate = DateRange.getObject(dateRange.start);
    const endDate = DateRange.getObject(dateRange.end);
    const hotelUrl = format(url, {
      startDate,
      endDate,
      roomCount,
      adultCount,
      childrenCount
    });

    this.setState({ loading: true });

    // The redirect page (book.hotelplugin.com) shows its copy in the widget's language and its
    // accent in the widget's primary color, which is Material-UI's 500 shade of the named color.
    window.parent.postMessage(
      {
        type: 'hp-book',
        url: hotelUrl,
        language,
        color: availableColors[color] && availableColors[color][500]
      },
      '*'
    );
  };

  render() {
    const { classes, translate, position, theme, color, url } = this.props;
    const { guestOptions, dateRange, height, loading } = this.state;

    return (
      <MuiThemeProvider theme={createTheme({ position, theme, color, height })}>
        <Measure bounds onResize={this.onResize}>
          {({ measureRef }) => (
            <RootRef rootRef={measureRef}>
              <Box position={position}>
                <div className={classes.container}>
                  <Grid container spacing={16}>
                    <Grid className={classes.dateColumn} item xs>
                      <DatePicker
                        label={translate({ text: 'checkIn' })}
                        value={dateRange.start}
                        prevDisabled={!dateRange.isValidStart(dateRange.prevStart)}
                        nextDisabled={!dateRange.isValidStart(dateRange.nextStart)}
                        onOpen={this.expand}
                        onChange={this.onCheckInDatePickerChange}
                        onBackClick={() => this.onDateRangeChange('start', dateRange.prevStart)}
                        onNextClick={() => this.onDateRangeChange('start', dateRange.nextStart)}
                        minDate={dateRange.minStart}
                        maxDate={dateRange.maxStart}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid className={classes.dateColumn} item xs>
                      <DatePicker
                        label={translate({ text: 'checkOut' })}
                        value={dateRange.end}
                        prevDisabled={!dateRange.isValidEnd(dateRange.prevEnd)}
                        nextDisabled={!dateRange.isValidEnd(dateRange.nextEnd)}
                        onOpen={this.expand}
                        onChange={this.onCheckOutDatePickerChange}
                        onBackClick={() => this.onDateRangeChange('end', dateRange.prevEnd)}
                        onNextClick={() => this.onDateRangeChange('end', dateRange.nextEnd)}
                        minDate={dateRange.minEnd}
                        maxDate={dateRange.maxEnd}
                        disabled={loading}
                        ref={this.checkOutDatePickerRef}
                      />
                    </Grid>
                    <Grid className={classes.guestColumn} item xs>
                      <GuestOptions
                        translate={translate}
                        label={translate({ text: 'guests' })}
                        hideChildrenCounter={!supportsChildrenCount(url)}
                        roomCount={guestOptions.roomCount}
                        adultCount={guestOptions.adultCount}
                        childrenCount={guestOptions.childrenCount}
                        onOpen={this.expand}
                        onClose={this.collapse}
                        onChange={this.onGuesOptionsChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs>
                      <AvailabilityButton onClick={this.onAvailabilityCheck} disabled={loading}>
                        {loading && <CircularProgress color="inherit" size={26} />}
                        {!loading && <Translate text="checkAvailability" />}
                      </AvailabilityButton>
                    </Grid>
                  </Grid>
                </div>
              </Box>
            </RootRef>
          )}
        </Measure>
      </MuiThemeProvider>
    );
  }
}

HotelPlugin.propTypes = {
  classes: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  url: PropTypes.string.isRequired,
  language: PropTypes.string,
  position: PropTypes.oneOf(['top', 'bottom']),
  theme: PropTypes.oneOf(['light', 'dark']),
  color: PropTypes.oneOf(Object.keys(availableColors))
};

HotelPlugin.defaultProps = {
  url: process.env.REACT_APP_HOTEL_URL,
  position: 'bottom',
  theme: 'light',
  color: 'blue'
};

export default withStyles(styles)(HotelPlugin);
